// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

import { sortLists } from './listsView'

// The API gates every /v1/salary-reviews route on the same roles as the route
// and nav item (SALARY_REVIEW_ROLES in ./salaryReviewAccess).

const messageOf = (error, fallback) => {
  const status = error.response && error.response.status
  const serverMessage = error.response && error.response.data && error.response.data.message
  if (serverMessage) return serverMessage
  // The role gate answers a bare "Forbidden"; say what it means instead.
  if (status === 403) return 'You do not have access to salary reviews.'
  return error.message || fallback
}

// Story 2.7: `quarter` ('YYYY-Qn') picks the quarter whose money and summary
// the API computes; without it the API uses the current quarter.
export const getLists = createAsyncThunk('salaryReviews/getLists', async (quarter, { rejectWithValue }) => {
  try {
    const response = quarter ? await axios.get('/v1/salary-reviews/lists', { params: { quarter } }) : await axios.get('/v1/salary-reviews/lists')
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not load the review lists.'))
  }
})

// Rejects with {status, message} so the dialog can put a 409 under the name.
export const createList = createAsyncThunk('salaryReviews/createList', async (body, { rejectWithValue }) => {
  try {
    const response = await axios.post('/v1/salary-reviews/lists', body)
    return response.data
  } catch (error) {
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      message: messageOf(error, 'Could not create the review list.')
    })
  }
})

// One list with its non-removed reviews (Story 2.2).
export const getList = createAsyncThunk('salaryReviews/getList', async (id, { rejectWithValue }) => {
  try {
    const response = await axios.get(`/v1/salary-reviews/lists/${id}`)
    return response.data
  } catch (error) {
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      message: messageOf(error, 'Could not load the review list.')
    })
  }
})

// One review with its history, marginInputs (Story 2.4) and the server's
// margin block (Story 2.5), for the review detail page. The page recomputes
// the margin from marginInputs with the UI mirror; the payload is kept
// exactly as sent.
export const getReview = createAsyncThunk('salaryReviews/getReview', async (id, { rejectWithValue }) => {
  try {
    const response = await axios.get(`/v1/salary-reviews/reviews/${id}`)
    return response.data
  } catch (error) {
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      message: messageOf(error, 'Could not load the review.')
    })
  }
})

// Active HRMS employees for the Add people picker.
export const getEmployees = createAsyncThunk('salaryReviews/getEmployees', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get('/v1/salary-reviews/employees')
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not load the employees.'))
  }
})

// Per-employee results [{userId, ok, reviewId?, error?}]; partial success is
// a fulfilled call. Story 2.8: `effectiveMonths` ({userId: 'YYYY-MM'}) sets
// each person's month inside the list's quarter.
export const addReviews = createAsyncThunk('salaryReviews/addReviews', async ({ listId, userIds, effectiveMonths }, { rejectWithValue }) => {
  try {
    const body = effectiveMonths && Object.keys(effectiveMonths).length ? { userIds, effectiveMonths } : { userIds }
    const response = await axios.post(`/v1/salary-reviews/lists/${listId}/reviews`, body)
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not add the people.'))
  }
})

// Rejects with {status, message}: a 409 means the row changed and the page
// reloads the list.
export const removeReview = createAsyncThunk('salaryReviews/removeReview', async ({ reviewId, rowVersion }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`/v1/salary-reviews/reviews/${reviewId}/actions/remove`, { rowVersion })
    return response.data
  } catch (error) {
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      message: messageOf(error, 'Could not remove the review.')
    })
  }
})

// Story 2.6: the changed fields plus rowVersion. Fulfils with the review as
// GET /reviews/:id returns it plus `warnings`; rejects with {status, message}
// plus {code} (STALE_REVIEW: the review changed, the page reloads it).
export const editReview = createAsyncThunk('salaryReviews/editReview', async ({ reviewId, body }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`/v1/salary-reviews/reviews/${reviewId}/actions/edit`, body)
    return response.data
  } catch (error) {
    // `code` is the API's machine-readable reason (STALE_REVIEW, COST_PLUS_ONLY).
    const code = error.response && error.response.data && error.response.data.code
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      code: typeof code === 'string' ? code : null,
      message: messageOf(error, 'Could not save the review.')
    })
  }
})

// Story 3.1: append a Talk / Note / Outcome communicated entry. Fulfils with
// the new entry as GET /reviews/:id lists it (masked for the viewer); rejects
// with {status, message}. No rowVersion: an entry changes nothing else.
export const addEntry = createAsyncThunk('salaryReviews/addEntry', async ({ reviewId, body }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`/v1/salary-reviews/reviews/${reviewId}/actions/addEntry`, body)
    return response.data
  } catch (error) {
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      message: messageOf(error, 'Could not add the entry.')
    })
  }
})

// Story 3.3: the Admin nav badge's count of reviews awaiting approval,
// across every list (GET /pending-count, Admin only). Fetched once per page
// load by the badge, and again after a decision or a move that changes it.
export const getPendingCount = createAsyncThunk('salaryReviews/getPendingCount', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get('/v1/salary-reviews/pending-count')
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not load the pending count.'))
  }
})

// Refresh the badge only once it has been loaded (it is an Admin's): an
// HR-only session never asks for it.
const refreshPendingCount = (dispatch, getState) => {
  const state = getState().salaryReviews
  if (state && state.pendingCountStatus !== 'idle') dispatch(getPendingCount())
}

// Story 2.9: remove a whole list with its reviews (HR or Admin). Fulfils
// with {listId, ok, removedReviews} and refreshes the Admin pending badge
// (the list's Awaiting approval reviews are gone); rejects with {status,
// code, message} (409: an approved / applied review, or the caller's own, is
// in it; 409 STALE_REVIEW: the list changed underneath).
export const removeList = createAsyncThunk('salaryReviews/removeList', async ({ listId }, { rejectWithValue, dispatch, getState }) => {
  try {
    const response = await axios.post(`/v1/salary-reviews/lists/${listId}/actions/remove`, {})
    refreshPendingCount(dispatch, getState)
    return response.data
  } catch (error) {
    const code = error.response && error.response.data && error.response.data.code
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      code: typeof code === 'string' ? code : null,
      message: messageOf(error, 'Could not remove the list.')
    })
  }
})

// Story 3.2: an HR status move (send, markNegotiating, withdraw) with the
// rowVersion the page saw. Fulfils with the review as GET /reviews/:id
// returns it, or {moved: true, reload: true} when the API moved it but could
// not read it back (the page reloads); rejects with {status, code, message} (409 STALE_REVIEW: the
// review changed, the page reloads it; 400: e.g. no proposed salary).
export const changeStatus = createAsyncThunk('salaryReviews/changeStatus', async ({ reviewId, action, rowVersion }, { rejectWithValue, dispatch, getState }) => {
  try {
    const response = await axios.post(`/v1/salary-reviews/reviews/${reviewId}/actions/${action}`, { rowVersion })
    // Sending and withdrawing change what awaits approval (Story 3.3 badge).
    refreshPendingCount(dispatch, getState)
    return response.data
  } catch (error) {
    const code = error.response && error.response.data && error.response.data.code
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      code: typeof code === 'string' ? code : null,
      message: messageOf(error, 'Could not change the review status.')
    })
  }
})

// Story 3.2: "Send N for approval" on a list: exactly the reviews the page
// shows, as [{reviewId, rowVersion}]. Fulfils with the per-review results
// [{reviewId, ok, error?}] (partial success is a fulfilled call); rejects
// with the message.
export const sendAll = createAsyncThunk('salaryReviews/sendAll', async ({ listId, reviews }, { rejectWithValue, dispatch, getState }) => {
  try {
    const response = await axios.post(`/v1/salary-reviews/lists/${listId}/actions/sendAll`, { reviews })
    refreshPendingCount(dispatch, getState)
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not send the reviews for approval.'))
  }
})

// Story 3.3: a decision on one review (approve, decline, counterOffer;
// acceptCounter for HR) with the rowVersion the page saw and the decision's
// fields ({reason} / {amount}). Fulfils like changeStatus (the review as GET
// /reviews/:id returns it, or {moved, reload}); rejects with {status, code,
// message}. Story 3.4: retryApply; Story 3.5: reopen ({reason}) and approve
// on a Declined review, whose 409 code OPEN_REVIEW_EXISTS the page shows
// without reloading. Story 3.6: overrideDiscipline ({discipline}: one of the
// 10, or null for the AI suggestion), tracked per review like a decision; it
// never changes the pending count.
// Story 5.1: the follow-up raise actions (addFollowUp, changeFollowUp,
// removeFollowUp, markFollowUpMet, dropFollowUp) go through here too, tracked
// per review like a decision; they never change the pending count either.
const KEEPS_PENDING_COUNT = ['overrideDiscipline', 'addFollowUp', 'changeFollowUp', 'removeFollowUp', 'markFollowUpMet', 'dropFollowUp']
export const decide = createAsyncThunk('salaryReviews/decide', async ({ reviewId, action, rowVersion, body }, { rejectWithValue, dispatch, getState }) => {
  try {
    const response = await axios.post(`/v1/salary-reviews/reviews/${reviewId}/actions/${action}`, { ...(body || {}), rowVersion })
    if (!KEEPS_PENDING_COUNT.includes(action)) refreshPendingCount(dispatch, getState)
    return response.data
  } catch (error) {
    const code = error.response && error.response.data && error.response.data.code
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      code: typeof code === 'string' ? code : null,
      message: messageOf(error, 'Could not save the decision.')
    })
  }
})

// Story 3.3: "Approve N meeting acceptable margin": exactly the reviews the
// page counted, as [{reviewId, rowVersion}]. Fulfils with the per-review
// results [{reviewId, ok, skipped?, error?}]; rejects with the message.
export const approveAllMeeting = createAsyncThunk('salaryReviews/approveAllMeeting', async ({ listId, reviews }, { rejectWithValue, dispatch, getState }) => {
  try {
    const response = await axios.post(`/v1/salary-reviews/lists/${listId}/actions/approveAllMeeting`, { reviews })
    refreshPendingCount(dispatch, getState)
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not approve the reviews.'))
  }
})

// Story 4.2: put a failed or unreachable notification of the review back in
// the queue (HR or Admin; the delivery job sends it). Fulfils with
// {deliveries} (the review's rows as GET /reviews/:id carries them); rejects
// with {status, message} (409: delivered or queued meanwhile; 404: gone).
export const resendDelivery = createAsyncThunk('salaryReviews/resendDelivery', async ({ reviewId, deliveryId }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`/v1/salary-reviews/reviews/${reviewId}/deliveries/${deliveryId}/resend`, {})
    return response.data
  } catch (error) {
    return rejectWithValue({
      status: (error.response && error.response.status) || null,
      message: messageOf(error, 'Could not resend the notification.')
    })
  }
})

// Story 3.5 review: end a review's in-flight decision, only when the answer
// is for the request that started it (a later one on the same review wins).
const clearDeciding = (state, action) => {
  const id = String(action.meta.arg.reviewId)
  const current = state.decidingById[id]
  if (current && current.requestId === action.meta.requestId) delete state.decidingById[id]
}

// Story 4.2 review: end a resend only when the answer is for its request.
const clearResending = (state, action) => {
  if (state.resendingDelivery && state.resendingDelivery.requestId === action.meta.requestId) state.resendingDelivery = null
}

/** The delivery whose resend is in flight on this review (null when none). */
export const resendingDeliveryOf = (resendingDelivery, reviewId) => {
  if (!resendingDelivery || reviewId === null || reviewId === undefined) return null
  return resendingDelivery.reviewId === String(reviewId) ? resendingDelivery.deliveryId : null
}

/** The action in flight on a review (null when none). */
export const decidingActionOf = (decidingById, reviewId) => {
  const entry = decidingById && reviewId !== null && reviewId !== undefined ? decidingById[String(reviewId)] : null
  return entry ? entry.action : null
}

const eventTime = (e) => {
  const t = e && e.at ? new Date(e.at).getTime() : NaN
  return Number.isNaN(t) ? -Infinity : t
}

/**
 * The incoming review's events plus any the loaded copy of the SAME review
 * has and the payload lacks (an entry added while an older read or edit was
 * in flight), newest first (at DESC, id DESC). History is append-only, so an
 * event never disappears.
 */
export const mergeEvents = (loaded, incoming) => {
  if (!incoming || !loaded || String(loaded.id) !== String(incoming.id)) return incoming
  const current = Array.isArray(loaded.events) ? loaded.events : []
  if (!current.length) return incoming
  const next = Array.isArray(incoming.events) ? incoming.events : []
  const known = new Set(next.map((e) => e.id))
  const merged = [...next, ...current.filter((e) => !known.has(e.id))]
  merged.sort((a, b) => eventTime(b) - eventTime(a) || Number(b.id || 0) - Number(a.id || 0))
  return { ...incoming, events: merged }
}

export const salaryReviewsSlice = createSlice({
  name: 'salaryReviews',
  initialState: {
    lists: null,
    // Story 2.7: the selected quarter's summary (GET /lists `quarters`), the
    // quarter the API answered for, and the latest request (older answers
    // are dropped).
    quarters: [],
    quarter: null,
    // Story 5.1: the overview's "Follow-ups due" (GET /lists `followUpsDue`).
    followUpsDue: [],
    listsRequestId: null,
    isLoading: false,
    error: null,
    isCreating: false,
    createError: null,
    // List detail (Story 2.2)
    detail: null,
    // The list id the latest getList asked for; older responses are dropped.
    detailRequestedId: null,
    detailLoading: false,
    detailError: null,
    // Review detail (Story 2.4): the review DTO as sent, marginInputs included
    // (absent when the salary is hidden from the viewer).
    review: null,
    reviewRequestedId: null,
    reviewLoading: false,
    reviewError: null,
    employees: null,
    employeesLoading: false,
    employeesError: null,
    isAdding: false,
    removingId: null,
    // Story 2.9: a list removal in flight; `listsStale` once a list was
    // removed: the quarter summaries still count it until the overview
    // reloads (it does on every mount).
    removingList: false,
    listsStale: false,
    // Story 2.6: an edit in flight
    savingReview: false,
    // Story 3.1: a history entry in flight
    addingEntry: false,
    // Story 3.2: the status move in flight (its action) and the review it is
    // on, and a list send
    changingStatus: null,
    changingStatusId: null,
    sendingAll: false,
    // Story 3.3: the decision in flight (its action) and the review it is
    // on; a bulk approve; the nav badge's count ('idle' until an Admin's
    // badge asks for it).
    // Story 3.5 review: the decision in flight per review id:
    // {[reviewId]: {action, requestId}}. One review's answer never clears or
    // overrides another's.
    decidingById: {},
    approvingAll: false,
    pendingCount: null,
    pendingCountStatus: 'idle',
    // The latest count request: an older answer never overwrites a newer
    // one (Story 3.3 review).
    pendingCountRequestId: null,
    // Story 4.2: the resend in flight: {reviewId, deliveryId, requestId}
    // (null when none). A row can show on several reviews of one action, so
    // it is tracked per review: another review's request never disables
    // this one's buttons, and only its own answer clears it.
    resendingDelivery: null
  },
  reducers: {
    clearCreateError: (state) => {
      state.createError = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLists.pending, (state, action) => {
        state.isLoading = true
        state.listsRequestId = action.meta.requestId
      })
      .addCase(getLists.fulfilled, (state, action) => {
        // A late answer for a quarter the page has moved away from.
        if (action.meta.requestId !== state.listsRequestId) return
        state.isLoading = false
        state.error = null
        // Story 2.7: {lists, quarters}; a bare array (older API) has no
        // quarter summaries.
        const payload = action.payload || {}
        const lists = Array.isArray(payload) ? payload : payload.lists || []
        state.lists = sortLists(lists)
        state.quarters = Array.isArray(payload.quarters) ? payload.quarters : []
        state.followUpsDue = Array.isArray(payload.followUpsDue) ? payload.followUpsDue : []
        state.listsStale = false
        state.quarter = Array.isArray(payload) ? null : payload.quarter || null
      })
      .addCase(getLists.rejected, (state, action) => {
        if (action.meta.requestId !== state.listsRequestId) return
        state.isLoading = false
        state.error = action.payload || action.error.message || 'Could not load the review lists.'
      })
      .addCase(createList.pending, (state) => {
        state.isCreating = true
        state.createError = null
      })
      // The new list joins its quarter without a reload.
      .addCase(createList.fulfilled, (state, action) => {
        state.isCreating = false
        state.lists = sortLists([...(state.lists || []).filter((l) => l.id !== action.payload.id), action.payload])
      })
      .addCase(createList.rejected, (state, action) => {
        state.isCreating = false
        state.createError = action.payload || {
          status: null,
          message: action.error.message || 'Could not create the review list.'
        }
      })
      .addCase(getList.pending, (state, action) => {
        state.detailLoading = true
        state.detailError = null
        state.detailRequestedId = String(action.meta.arg)
        // Another list's data must never show under this one's header.
        if (state.detail && String(state.detail.id) !== String(action.meta.arg)) state.detail = null
      })
      .addCase(getList.fulfilled, (state, action) => {
        // A late answer for a list the page has moved away from.
        if (String(action.meta.arg) !== state.detailRequestedId) return
        state.detailLoading = false
        state.detailError = null
        state.detail = action.payload
      })
      .addCase(getList.rejected, (state, action) => {
        if (String(action.meta.arg) !== state.detailRequestedId) return
        state.detailLoading = false
        state.detailError = action.payload || {
          status: null,
          message: action.error.message || 'Could not load the review list.'
        }
      })
      .addCase(getReview.pending, (state, action) => {
        state.reviewLoading = true
        state.reviewError = null
        state.reviewRequestedId = String(action.meta.arg)
        if (state.review && String(state.review.id) !== String(action.meta.arg)) state.review = null
      })
      .addCase(getReview.fulfilled, (state, action) => {
        if (String(action.meta.arg) !== state.reviewRequestedId) return
        state.reviewLoading = false
        state.review = mergeEvents(state.review, action.payload)
      })
      .addCase(getReview.rejected, (state, action) => {
        if (String(action.meta.arg) !== state.reviewRequestedId) return
        state.reviewLoading = false
        // A failed reload must not leave the previous figures on screen.
        state.review = null
        state.reviewError = action.payload || {
          status: null,
          message: action.error.message || 'Could not load the review.'
        }
      })
      .addCase(editReview.pending, (state) => {
        state.savingReview = true
      })
      // The saved review replaces the loaded one (warnings stay with the
      // caller); a late answer for a review the page left is dropped.
      .addCase(editReview.fulfilled, (state, action) => {
        state.savingReview = false
        if (String(action.meta.arg.reviewId) !== state.reviewRequestedId) return
        const { warnings, ...review } = action.payload || {}
        state.review = mergeEvents(state.review, review)
        state.reviewError = null
      })
      .addCase(editReview.rejected, (state) => {
        state.savingReview = false
      })
      .addCase(addEntry.pending, (state) => {
        state.addingEntry = true
      })
      // The new entry goes to the top of the history, no reload; a late
      // answer for a review the page left is dropped.
      .addCase(addEntry.fulfilled, (state, action) => {
        state.addingEntry = false
        const review = state.review
        if (!review || String(review.id) !== String(action.meta.arg.reviewId) || !action.payload) return
        const events = Array.isArray(review.events) ? review.events : []
        review.events = [action.payload, ...events.filter((e) => e.id !== action.payload.id)]
      })
      .addCase(addEntry.rejected, (state) => {
        state.addingEntry = false
      })
      .addCase(changeStatus.pending, (state, action) => {
        state.changingStatus = action.meta.arg.action
        state.changingStatusId = String(action.meta.arg.reviewId)
      })
      // The moved review replaces the loaded one (its StatusChanged entry
      // included); a late answer for a review the page left is dropped.
      .addCase(changeStatus.fulfilled, (state, action) => {
        if (state.changingStatusId === String(action.meta.arg.reviewId)) {
          state.changingStatus = null
          state.changingStatusId = null
        }
        // {moved, reload}: nothing to show; the page reloads the review.
        if (String(action.meta.arg.reviewId) !== state.reviewRequestedId || !action.payload || action.payload.reload) return
        state.review = mergeEvents(state.review, action.payload)
        state.reviewError = null
      })
      .addCase(changeStatus.rejected, (state, action) => {
        if (state.changingStatusId === String(action.meta.arg.reviewId)) {
          state.changingStatus = null
          state.changingStatusId = null
        }
      })
      .addCase(sendAll.pending, (state) => {
        state.sendingAll = true
      })
      .addCase(sendAll.fulfilled, (state) => {
        state.sendingAll = false
      })
      .addCase(sendAll.rejected, (state) => {
        state.sendingAll = false
      })
      .addCase(decide.pending, (state, action) => {
        state.decidingById[String(action.meta.arg.reviewId)] = { action: action.meta.arg.action, requestId: action.meta.requestId }
      })
      // The decided review replaces the loaded one (its decision entry
      // included); a late answer for a review the page left is dropped.
      .addCase(decide.fulfilled, (state, action) => {
        clearDeciding(state, action)
        if (String(action.meta.arg.reviewId) !== state.reviewRequestedId || !action.payload || action.payload.reload) return
        state.review = mergeEvents(state.review, action.payload)
        state.reviewError = null
      })
      .addCase(decide.rejected, (state, action) => {
        clearDeciding(state, action)
      })
      .addCase(approveAllMeeting.pending, (state) => {
        state.approvingAll = true
      })
      .addCase(approveAllMeeting.fulfilled, (state) => {
        state.approvingAll = false
      })
      .addCase(approveAllMeeting.rejected, (state) => {
        state.approvingAll = false
      })
      .addCase(resendDelivery.pending, (state, action) => {
        state.resendingDelivery = {
          reviewId: String(action.meta.arg.reviewId),
          deliveryId: action.meta.arg.deliveryId,
          requestId: action.meta.requestId
        }
      })
      // The review's notifications as the API now lists them; a late answer
      // for a review the page left changes nothing on screen.
      .addCase(resendDelivery.fulfilled, (state, action) => {
        clearResending(state, action)
        const review = state.review
        if (!review || String(review.id) !== String(action.meta.arg.reviewId)) return
        if (action.payload && Array.isArray(action.payload.deliveries)) review.deliveries = action.payload.deliveries
      })
      .addCase(resendDelivery.rejected, (state, action) => {
        clearResending(state, action)
      })
      .addCase(getPendingCount.pending, (state, action) => {
        state.pendingCountRequestId = action.meta.requestId
        if (state.pendingCountStatus === 'idle') state.pendingCountStatus = 'loading'
      })
      .addCase(getPendingCount.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.pendingCountRequestId) return
        state.pendingCountStatus = 'loaded'
        const count = Number(action.payload && action.payload.count)
        state.pendingCount = Number.isFinite(count) && count > 0 ? count : 0
      })
      // A failed count shows no badge (never a stale or made-up number).
      .addCase(getPendingCount.rejected, (state, action) => {
        if (action.meta.requestId !== state.pendingCountRequestId) return
        state.pendingCountStatus = 'failed'
        state.pendingCount = null
      })
      .addCase(getEmployees.pending, (state) => {
        state.employeesLoading = true
        state.employeesError = null
      })
      .addCase(getEmployees.fulfilled, (state, action) => {
        state.employeesLoading = false
        state.employees = action.payload
      })
      .addCase(getEmployees.rejected, (state, action) => {
        state.employeesLoading = false
        state.employeesError = action.payload || action.error.message || 'Could not load the employees.'
      })
      .addCase(addReviews.pending, (state) => {
        state.isAdding = true
      })
      .addCase(addReviews.fulfilled, (state) => {
        state.isAdding = false
      })
      .addCase(addReviews.rejected, (state) => {
        state.isAdding = false
      })
      .addCase(removeReview.pending, (state, action) => {
        state.removingId = action.meta.arg.reviewId
      })
      // Drop the row at once; the page reloads the list for fresh counts.
      .addCase(removeReview.fulfilled, (state, action) => {
        state.removingId = null
        if (state.detail && Array.isArray(state.detail.reviews)) {
          state.detail.reviews = state.detail.reviews.filter((r) => r.id !== action.meta.arg.reviewId)
        }
      })
      .addCase(removeReview.rejected, (state) => {
        state.removingId = null
      })
      .addCase(removeList.pending, (state) => {
        state.removingList = true
      })
      // The list is gone: drop it from the overview and the detail at once.
      .addCase(removeList.fulfilled, (state, action) => {
        const listId = String(action.meta.arg.listId)
        state.removingList = false
        if (Array.isArray(state.lists)) state.lists = state.lists.filter((l) => String(l.id) !== listId)
        state.listsStale = true
        if (state.detail && String(state.detail.id) === listId) state.detail = null
      })
      .addCase(removeList.rejected, (state) => {
        state.removingList = false
      })
  }
})

export const { clearCreateError } = salaryReviewsSlice.actions

export default salaryReviewsSlice.reducer
